/**
 * Iris Topics Explorer + Trends — the Fin-style "what are people talking to the
 * AI agent about" view. Conversations are auto-classified into topics
 * (`SmsConversation.topic`); this surfaces per-topic volume + quality metrics
 * and week-over-week trend alerts (spikes / drops / newly-emerging topics).
 *
 * Proxied through operations (the FE's auth boundary + RBAC gate) at
 * `/api/messaging/iris/topics/*`, which forwards to ai-operations with the
 * shared service key. Powers Settings → Iris → Analyze.
 */
import { useQuery } from '@tanstack/react-query';
import { apiGet, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/topics';

/** One row of the Topics Explorer: a cluster of conversations plus its
 * quality metrics over the selected window. Rates are fractions in [0,1] and
 * null when there aren't enough analyzed conversations to compute them. */
export interface TopicRow {
  topic: string;
  /** Number of analyzed conversations classified into this topic. */
  volume: number;
  /** Resolved ÷ analyzable; null when nothing to measure. */
  resolutionRate: number | null;
  /** Handed-off-to-human ÷ analyzable; null when nothing to measure. */
  escalationRate: number | null;
  /** Mean CX score (1–5) across rated conversations; null when none rated. */
  avgCxScore: number | null;
}

export interface TopicsExploreResult {
  windowDays: number;
  /** Total analyzed conversations across all topics in the window. */
  totalConversations: number;
  /** Topics, already sorted by volume desc by the backend (we re-sort defensively). */
  topics: TopicRow[];
}

/** Direction of a topic trend alert. */
export type TopicTrendKind = 'SPIKE' | 'DROP' | 'NEW';

/**
 * A week-over-week trend alert for a single topic. `current`/`previous` are the
 * metric values for this and the prior comparable window; `deltaPct` is the
 * fractional change (e.g. 0.5 = +50%), null for NEW topics with no prior data.
 */
export interface TopicTrend {
  topic: string;
  kind: TopicTrendKind;
  /** Which metric moved — usually 'volume', but the backend may flag others. */
  metric: string;
  current: number;
  previous: number;
  /** current − previous, in the metric's native units. */
  delta: number;
  /** Fractional change vs previous; null when previous is 0 / topic is NEW. */
  deltaPct: number | null;
}

export interface TopicsTrendsResult {
  windowDays: number;
  trends: TopicTrend[];
}

export const irisTopicsExploreKey = (days: number) =>
  ['messaging', 'iris-topics-explore', days] as const;

export const irisTopicsTrendsKey = (days: number) =>
  ['messaging', 'iris-topics-trends', days] as const;

/** Per-topic volume + quality metrics over the last `days`. */
export function useTopicsExplore(days = 30) {
  return useQuery({
    queryKey: irisTopicsExploreKey(days),
    queryFn: () => apiGet<TopicsExploreResult>(operationsClient, `${BASE}/explore?days=${days}`),
    staleTime: 30_000,
  });
}

/** Week-over-week trend alerts (spikes / drops / newly-emerging topics). */
export function useTopicTrends(days = 30) {
  return useQuery({
    queryKey: irisTopicsTrendsKey(days),
    queryFn: () => apiGet<TopicsTrendsResult>(operationsClient, `${BASE}/trends?days=${days}`),
    staleTime: 30_000,
  });
}
